import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, X } from 'lucide-react';
import axios from 'axios';
import { useAuthStore } from '../stores/authStore';
import DiscussionList from '../components/discussions/DiscussionList';

export default function Discussions() {
  const [searchParams] = useSearchParams();
  const [showNewForm, setShowNewForm] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const { token } = useAuthStore();

  // Get discussionable type and id from URL params
  const discussionableType = searchParams.get('type');
  const discussionableId = searchParams.get('id');

  // Auto-open form if coming from a filtered view
  useEffect(() => {
    if (discussionableType && discussionableId && !showNewForm) {
      setShowNewForm(true);
    }
  }, [discussionableType, discussionableId]);

  const handleCreate = async () => {
    if (!title.trim() || !content.trim()) {
      alert('Please fill in both title and content');
      return;
    }

    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8000/api'}/discussions`,
        {
          title,
          content,
          discussionable_type: discussionableType ? `App\\Models\\${discussionableType}` : null,
          discussionable_id: discussionableId ? parseInt(discussionableId) : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTitle('');
      setContent('');
      setShowNewForm(false);
      setRefreshKey(k => k + 1); // Refresh the list
    } catch (error) {
      alert('Failed to create discussion');
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Discussions</h1>
          <p className="mt-2 text-gray-600">
            Ask questions, share insights, and connect with fellow learners
          </p>
        </div>
        <button
          onClick={() => setShowNewForm(!showNewForm)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          {showNewForm ? (
            <>
              <X className="h-5 w-5" />
              Cancel
            </>
          ) : (
            <>
              <Plus className="h-5 w-5" />
              New Discussion
            </>
          )}
        </button>
      </div>

      {/* New discussion form */}
      {showNewForm && (
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <h3 className="text-lg font-semibold mb-4">Start a new discussion</h3>
          
          {discussionableType && discussionableId && (
            <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
              <p className="text-sm text-indigo-800">
                💬 This discussion will be linked to the {discussionableType.toLowerCase()} you were viewing
              </p>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="What's your discussion about?"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Content
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Share your thoughts, ask a question, or start a conversation..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows={6}
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleCreate}
                disabled={!title.trim() || !content.trim()}
                className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Discussion
              </button>
              <button
                onClick={() => {
                  setShowNewForm(false);
                  setTitle('');
                  setContent('');
                }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Discussion list */}
      <DiscussionList key={refreshKey} />
    </div>
  );
}
